// Utils/methods.js
import REG_EX from 'Utils/Constants/RegEx'
import COFFEE_COMMUNITIES_CONSTANTS from './Constants'

const { MAX_PREVIEW_CHARS, MAX_FILE_SIZE, MAX_FILES, RETRY_DELAY, MAX_RETRIES, BUCKET_NAME } = COFFEE_COMMUNITIES_CONSTANTS

export const COFFEE_COMMUNITIES_METHODS = {
  timeMethods: {
    UtcToLocalTimeFn(utcString, use12h) {
      const date = new Date(utcString)
      const options = { hour: '2-digit', minute: '2-digit', hour12: use12h }

      return date.toLocaleTimeString('en-US', options)
    }
  },

  textMethods: {
    convertLinksToAnchors(text) {
      if (!text) return ''

      return text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="message-link">$1</a>')
    },

    buildPreviewText(rawText, expanded) {
      const text = (rawText || '').toString()
      const isLong = text.trim().length > MAX_PREVIEW_CHARS
      const preview = isLong && !expanded ? `${text.slice(0, MAX_PREVIEW_CHARS).trim()}...` : text

      return { isLong, preview }
    },

    autoResizeTextarea(textarea) {
      if (!textarea) return

      const textAreaRef = textarea
      const style = textAreaRef.style

      style.height = 'auto'
      const CAP = COFFEE_COMMUNITIES_CONSTANTS.TEXTAREA_MAX_HEIGHT
      const newHeight = Math.min(textAreaRef.scrollHeight, CAP)

      style.height = `${newHeight}px`
      style.overflowY = textAreaRef.scrollHeight > CAP ? 'auto' : 'hidden'
    },

    normalizeUrl(url) {
      if (!url) return ''

      return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
    },

    // Highlighting method for the start name with @ for internal communities
    convertMentionsToHighlighted(text, members = []) {
      if (!text) return ''

      const names = [['everyone', 'Everyone']]

      members?.forEach((member) => {
        const firstName = (member.first_name || '').trim()
        const lastName = (member.last_name || '').trim()

        if (firstName) {
          if (lastName) {
            names.push([`${firstName} ${lastName}`.toLowerCase(), `${firstName} ${lastName}`])
          }
          names.push([firstName.toLowerCase(), firstName])
        }
      })

      // Helper function to render mention HTML
      const renderMention = (spaceBefore, displayName, caption = '') => {
        const captionHtml = caption ? ` ${caption}` : ''

        return `${spaceBefore}<span class="mention-at">@</span><span class="mention-highlight">${displayName}</span><span class="mention-caption">${captionHtml}</span>`
      }

      let mentionCount = 0

      return text.replace(REG_EX.MENTION_PATTERN, (fullMatch, textAfterAt) => {
        const spaceBefore = mentionCount > 0 ? ' ' : ''

        mentionCount += 1
        const trimmedText = textAfterAt.trim()

        if (!trimmedText) return fullMatch

        const lowerText = trimmedText.toLowerCase()
        const matchedEntry = names.find(([nameKey]) => lowerText.startsWith(nameKey))

        if (matchedEntry) {
          const [nameKey, displayName] = matchedEntry
          const nameWords = nameKey.split(' ')
          let matchedLength = 0

          const allWordsMatched = nameWords.every((word, wordIndex) => {
            if (!lowerText.substring(matchedLength).startsWith(word)) return false
            matchedLength += word.length
            if (wordIndex < nameWords.length - 1) {
              const spacesMatch = trimmedText.substring(matchedLength).match(REG_EX.LEADING_SPACES_ONE_OR_MORE)

              if (spacesMatch) {
                matchedLength += spacesMatch[0].length

                return true
              }

              return false
            }

            return true
          })

          if (allWordsMatched && matchedLength > 0) {
            const textAfterName = trimmedText.substring(matchedLength)

            if (!textAfterName || REG_EX.MENTION_NAME_DELIMITER.test(textAfterName)) {
              return renderMention(spaceBefore, displayName, textAfterName.trim())
            }
          }
        }

        return fullMatch
      })
    }
  },

  userMethods: {
    createDefaultUser(userId) {
      return {
        userid: userId,
        first_name: 'User',
        last_name: '',
        users_flag_url: ''
      }
    }
  },

  attachmentMethods: {
    sanitizeFileName(name) {
      if (!name) return ''

      return name
        .replace(/[^\w\s\-.]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
    },

    isMediaAttachment(mimeType) {
      return mimeType?.startsWith('image/') || mimeType?.startsWith('video/')
    },

    isImageAttachment(mimeType) {
      return mimeType?.startsWith('image/')
    },

    isVideoAttachment(mimeType) {
      return mimeType?.startsWith('video/')
    }
  }
}
